class ProductMedia extends HTMLElement {
  constructor() {
    super();
    this.mainSlider = null;
    this.thumbsSlider = null;
    this.init();
  }

  init() {
    if (typeof Splide === 'undefined') {
      console.error('Splide library not loaded');
      return;
    }
    this.initSliders();
  }

  initSliders() {
    const mainSliderEl = this.querySelector('[data-main-product-slider]');
    const thumbsSliderEl = this.querySelector('[data-main-product-thumbs]');
    
    if (!mainSliderEl) return;

    const loopEnabled = this.dataset.loop === 'true';
    const slidesCount = parseInt(mainSliderEl.dataset.slidesCount) || 0;
    const isDesktopSlider = mainSliderEl.hasAttribute('data-main-slider-desktop');
    const hasAdaptiveHeight = this.hasAttribute('data-autoheight');

    // Initialize thumbnails slider
    if (thumbsSliderEl) {
      this.thumbsSlider = new Splide(thumbsSliderEl, {
        type: loopEnabled ? 'loop' : 'slide',
        rewind: !loopEnabled,
        direction: 'ttb',
        height: 'auto',
        perPage: 4,
        perMove: 1,
        gap: 12,
        pagination: false,
        arrows: false,
        isNavigation: true,
        focus: 'center',
        trimSpace: false,
        updateOnMove: true,
        breakpoints: {
          990: {
            destroy: true
          }
        }
      });
    }

    // Main slider configuration
    const mainConfig = {
      type: loopEnabled ? 'loop' : 'slide',
      rewind: !loopEnabled && slidesCount > 1,
      perPage: 1,
      perMove: 1,
      gap: 0,
      pagination: false,
      arrows: false,
      drag: true,
      speed: 600,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      autoHeight: hasAdaptiveHeight,
      trimSpace: false,
      focus: 'center',
      updateOnMove: true,
      keyboard: 'global',
      breakpoints: {
        990: {
          destroy: isDesktopSlider
        }
      }
    };

    this.mainSlider = new Splide(mainSliderEl, mainConfig);

    // Sync sliders
    if (this.thumbsSlider) {
      this.mainSlider.sync(this.thumbsSlider);
      this.thumbsSlider.mount();
    }

    this.mainSlider.mount();

    // Setup UI controls
    this.setupControls();
    this.setupProgressBar();
    this.handleMediaPlayback();
  }

  setupControls() {
    const controls = this.querySelectorAll('.main-product__media-control');
    
    controls.forEach((control, index) => {
      control.addEventListener('click', () => {
        if (this.mainSlider) {
          index === 0 ? this.mainSlider.go('<') : this.mainSlider.go('>');
        }
      });
    });

    // Thumbnail next button
    const thumbsNext = this.querySelector('[data-thumbs-next]');
    if (thumbsNext && this.thumbsSlider) {
      thumbsNext.addEventListener('click', () => {
        this.thumbsSlider.go('>');
      });
    }
  }

  setupProgressBar() {
    const progressBar = this.querySelector('[data-main-product-slider-progress-bar]');
    
    if (!progressBar || !this.mainSlider) return;

    this.mainSlider.on('mounted move', () => {
      const end = this.mainSlider.Components.Controller.getEnd() + 1;
      const rate = Math.min((this.mainSlider.index + 1) / end, 1);
      progressBar.style.width = `${rate * 100}%`;
    });
  }

  handleMediaPlayback() {
    if (!this.mainSlider) return;

    this.mainSlider.on('move', () => {
      this.pauseAllMedia();
    });
  }

  pauseAllMedia() {
    this.querySelectorAll('video, iframe').forEach(media => {
      if (media.tagName === 'VIDEO') {
        media.pause();
      } else if (media.tagName === 'IFRAME') {
        if (media.classList.contains('js-youtube')) {
          media.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
        } else if (media.classList.contains('js-vimeo')) {
          media.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.mainSlider) {
      this.mainSlider.destroy();
    }
    if (this.thumbsSlider) {
      this.thumbsSlider.destroy();
    }
  }
}

customElements.define('product-media', ProductMedia);

// Handle variant changes
document.addEventListener('DOMContentLoaded', () => {
  const productForm = document.querySelector('product-form');
  
  if (productForm) {
    productForm.addEventListener('variant-change', (event) => {
      const variant = event.detail?.variant;
      const productMedia = document.querySelector('product-media');
      
      if (!variant?.featured_media || !productMedia?.mainSlider) return;

      const targetSlide = productMedia.querySelector(
        `[data-media-id="${variant.featured_media.id}"]`
      );

      if (targetSlide) {
        const slideIndex = Array.from(targetSlide.parentElement.children).indexOf(targetSlide);
        productMedia.mainSlider.go(slideIndex);
      }
    });
  }
});

// Product Media Gallery Modal
class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.setupModal();
  }

  setupModal() {
    const closeButton = this.querySelector('[id^="ModalClose"]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    this.addEventListener('click', (e) => {
      if (e.target === this) {
        this.hide();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.hide();
      }
    });
  }

  show(opener) {
    this.openedBy = opener;
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    
    const mediaId = opener?.dataset?.mediaId;
    if (mediaId) {
      this.showMedia(mediaId);
    }

    this.querySelector('.main-product__gallery-modal-content')?.focus();
  }

  hide() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.pauseAllMedia();
    
    if (this.openedBy) {
      this.openedBy.focus();
    }
  }

  showMedia(id) {
    const galleryMedia = this.querySelector(`[data-media-id="${id}"]`);
    if (!galleryMedia) return;

    if (window.innerWidth < 990) {
      this.showMediaMobile(galleryMedia);
    } else {
      this.showMediaDesktop(galleryMedia);
    }
  }

  showMediaMobile(galleryMedia) {
    this.querySelectorAll('[data-media-id]').forEach(media => {
      media.style.display = 'none';
    });
    
    galleryMedia.style.display = 'block';

    const mediaTrigger = galleryMedia.querySelector('button');
    if (mediaTrigger) {
      mediaTrigger.click();
      this.playMediaIfVideo(galleryMedia);
    }
  }

  showMediaDesktop(galleryMedia) {
    this.querySelectorAll('[data-media-id]').forEach(media => {
      media.style.display = 'block';
    });
    
    galleryMedia.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'nearest',
      inline: 'center'
    });
  }

  playMediaIfVideo(container) {
    const iframe = container.querySelector('iframe');
    if (!iframe) return;

    if (iframe.classList.contains('js-youtube')) {
      iframe.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      );
    } else if (iframe.classList.contains('js-vimeo')) {
      iframe.contentWindow.postMessage('{"method":"play"}', '*');
    }
  }

  pauseAllMedia() {
    this.querySelectorAll('video, iframe').forEach(media => {
      if (media.tagName === 'VIDEO') {
        media.pause();
      } else if (media.tagName === 'IFRAME') {
        if (media.classList.contains('js-youtube')) {
          media.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
        } else if (media.classList.contains('js-vimeo')) {
          media.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }
    });
  }
}

customElements.define('product-media-gallery', ProductMediaGallery);

// Setup gallery triggers
document.addEventListener('DOMContentLoaded', () => {
  const galleryTriggers = document.querySelectorAll('[data-open-gallery]');
  const gallery = document.querySelector('product-media-gallery');
  
  if (gallery) {
    galleryTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        gallery.show(trigger);
      });
    });
  }
});